/**
 * AR deck presentation theme.
 *
 * The theme is stored per deck (`AssessmentReview.theme` in the backend) and is
 * picked by the expert. Every viewer of that deck renders the theme stored on
 * it: the expert draft/preview, the client facing
 * `/app/ass-report/preview/:id` view and the generated PDF.
 *
 * The value lives in this module instead of being threaded through props
 * because ~40 components buried in the deck read it through `isDarkDeck()` -
 * they were already reading a marketing-site allowlist from this same style of
 * call. `components/assessmentReview/wrapper.js` seeds the theme from
 * `arDetail.theme` before the deck renders and re-renders the deck when the
 * expert toggles it, so every reader picks up the new value.
 */

export const AR_DECK_THEME = {
  light: 'light',
  dark: 'dark'
}

let currentDeckTheme = AR_DECK_THEME.light

const listeners = new Set()

const normalize = (theme) =>
  theme === AR_DECK_THEME.dark ? AR_DECK_THEME.dark : AR_DECK_THEME.light

// Applied on <body> so the global stylesheets follow the deck:
// App.sass (#root/.App background), view/client/index.sass (.right) and the
// header (index.sass + app header).
const applyToBody = () => {
  if (typeof document === 'undefined' || !document.body) return
  const dark = currentDeckTheme === AR_DECK_THEME.dark
  document.body.classList.toggle('deck-dark', dark)
  document.body.setAttribute('data-deck-theme', currentDeckTheme)
}

export const getDeckTheme = () => currentDeckTheme

export const isDarkDeck = () => currentDeckTheme === AR_DECK_THEME.dark

/**
 * Sets the deck theme, updates <body> and notifies subscribers (the app header
 * follows the deck). Falls back to 'light' for any unexpected value.
 */
export const setDeckTheme = (theme) => {
  currentDeckTheme = normalize(theme)
  applyToBody()
  listeners.forEach((listener) => listener(currentDeckTheme))
  return currentDeckTheme
}

/** subscribe(() => ...) - used by the header to repaint when the theme changes. */
export const subscribeDeckTheme = (listener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
